// ===== ANONYMOUS AI v25.0 QUANTUM - MEMORY SERVICE =====

import localforage from 'localforage';

// Configure localForage
localforage.config({
  name: 'AnonymousAI',
  storeName: 'conversations',
  description: 'Persistent memory for Anonymous AI v25.0'
});

class MemoryService {
  constructor() {
    this.conversationKey = 'current_conversation';
    this.historyKey = 'conversation_history';
    this.maxMessages = parseInt(process.env.REACT_APP_MAX_MEMORY_MESSAGES) || 100;
  }

  /**
   * Save current conversation to memory
   */
  async saveConversation(messages) {
    try {
      await localforage.setItem(this.conversationKey, {
        messages,
        timestamp: Date.now(),
        messageCount: messages.length
      });
      
      // Also save to history
      await this.addToHistory(messages);
      
      return true;
    } catch (error) {
      console.error('Failed to save conversation:', error);
      return false;
    }
  }

  /**
   * Load current conversation from memory
   */
  async loadConversation() {
    try {
      const data = await localforage.getItem(this.conversationKey);
      
      if (!data) return [];
      
      // Limit to max messages
      const messages = data.messages || [];
      return messages.slice(-this.maxMessages);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      return [];
    }
  }

  /**
   * Clear current conversation
   */
  async clearConversation() {
    try {
      await localforage.removeItem(this.conversationKey);
      return true;
    } catch (error) {
      console.error('Failed to clear conversation:', error);
      return false;
    }
  }

  /**
   * Add conversation to history
   */
  async addToHistory(messages) {
    try {
      const history = await this.getHistory();
      
      const conversationSummary = {
        id: Date.now(),
        timestamp: Date.now(),
        messageCount: messages.length,
        preview: this.generatePreview(messages),
        messages: messages.slice(-50) // Store last 50 messages per conversation
      };
      
      history.unshift(conversationSummary);
      
      // Keep only last 20 conversations
      const trimmedHistory = history.slice(0, 20);
      
      await localforage.setItem(this.historyKey, trimmedHistory);
      
      return true;
    } catch (error) {
      console.error('Failed to add to history:', error);
      return false;
    }
  }

  /**
   * Get conversation history
   */
  async getHistory() {
    try {
      const history = await localforage.getItem(this.historyKey);
      return history || [];
    } catch (error) {
      console.error('Failed to get history:', error);
      return [];
    }
  }

  /**
   * Load conversation from history
   */
  async loadFromHistory(conversationId) {
    try {
      const history = await this.getHistory();
      const conversation = history.find(c => c.id === conversationId);
      
      if (conversation) {
        return conversation.messages;
      }
      
      return [];
    } catch (error) {
      console.error('Failed to load from history:', error);
      return [];
    }
  }

  /**
   * Delete conversation from history
   */
  async deleteFromHistory(conversationId) {
    try {
      const history = await this.getHistory();
      const filtered = history.filter(c => c.id !== conversationId);
      
      await localforage.setItem(this.historyKey, filtered);
      return true;
    } catch (error) {
      console.error('Failed to delete from history:', error);
      return false;
    }
  }

  /**
   * Clear all history
   */
  async clearHistory() {
    try {
      await localforage.removeItem(this.historyKey);
      return true;
    } catch (error) {
      console.error('Failed to clear history:', error);
      return false;
    }
  }

  /**
   * Generate preview from messages
   */
  generatePreview(messages) {
    if (!messages || messages.length === 0) return 'Empty conversation';
    
    const firstUserMessage = messages.find(m => m.role === 'user');
    if (firstUserMessage) {
      return firstUserMessage.content.substring(0, 100) + 
             (firstUserMessage.content.length > 100 ? '...' : '');
    }
    
    return 'No user messages';
  }

  /**
   * Get memory statistics
   */
  async getStats() {
    try {
      const conversation = await localforage.getItem(this.conversationKey);
      const history = await this.getHistory();
      
      return {
        currentMessages: conversation?.messages?.length || 0,
        totalConversations: history.length,
        totalMessages: history.reduce((acc, conv) => acc + conv.messageCount, 0),
        oldestConversation: history[history.length - 1]?.timestamp || null,
        newestConversation: history[0]?.timestamp || null
      };
    } catch (error) {
      console.error('Failed to get stats:', error);
      return null;
    }
  }

  /**
   * Export all data
   */
  async exportData() {
    try {
      const conversation = await localforage.getItem(this.conversationKey);
      const history = await this.getHistory();
      
      return {
        currentConversation: conversation,
        history,
        exportDate: new Date().toISOString(),
        version: '25.0'
      };
    } catch (error) {
      console.error('Failed to export data:', error);
      return null;
    }
  }

  /**
   * Import data
   */
  async importData(data) {
    try {
      if (data.currentConversation) {
        await localforage.setItem(this.conversationKey, data.currentConversation);
      }
      
      if (data.history) {
        await localforage.setItem(this.historyKey, data.history);
      }
      
      return true;
    } catch (error) {
      console.error('Failed to import data:', error);
      return false;
    }
  }

  /**
   * Search through conversations
   */
  async search(query) {
    try {
      const history = await this.getHistory();
      const lowerQuery = query.toLowerCase();
      
      return history.filter(conv => {
        return conv.messages.some(msg => 
          msg.content.toLowerCase().includes(lowerQuery)
        );
      });
    } catch (error) {
      console.error('Failed to search:', error);
      return [];
    }
  }
}

const memoryServiceInstance = new MemoryService();
export default memoryServiceInstance;

